import { apiClient, getApiUrl } from '../lib/apiClient';
import type { 
  Alert,
  Pagination,
  ListQueryParams 
} from '../types/api';
import { 
  extractListData, 
  extractSingleData, 
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse 
} from '../utils/railsResponseHandler';

export interface AlertStats {
  total: number;
  active: number;
  critical: number;
  resolved: number;
  dismissed: number;
}

// Get alerts
export const getAlerts = async (params?: {
  severity?: string;
  status?: string;
  alert_type?: string;
  school_id?: string;
} & ListQueryParams): Promise<{ alerts: Alert[]; pagination: Pagination }> => {
  const response = await apiClient.get<RailsWrappedListResponse<Alert>>(getApiUrl('/alerts'), { params });
  
  const alerts = extractListData<Alert>(response.data);
  const pagination = extractPagination(response.data) || {
    current_page: 1,
    total_pages: 1,
    total_count: alerts.length,
    per_page: 20,
    has_previous: false,
    has_next: false
  };
  
  return { alerts, pagination };
};

// Get alert by ID
export const getAlert = async (id: string): Promise<Alert> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<Alert>>(getApiUrl(`/alerts/${id}`));
  return extractSingleData<Alert>(response.data);
};

// Get alert statistics
export const getAlertStats = async (params?: {
  school_id?: string;
  date_range?: string;
}): Promise<AlertStats> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<AlertStats>>(getApiUrl('/alerts/stats'), { params });
  return extractSingleData<AlertStats>(response.data);
};

// Acknowledge alert
export const acknowledgeAlert = async (id: string, notes?: string): Promise<Alert> => {
  const response = await apiClient.put<RailsSingleWrappedResponse<Alert>>(getApiUrl(`/alerts/${id}/acknowledge`), { notes });
  return extractSingleData<Alert>(response.data);
};

// Resolve alert
export const resolveAlert = async (id: string, resolution: {
  notes: string;
  action_taken?: string;
}): Promise<Alert> => {
  const response = await apiClient.put<RailsSingleWrappedResponse<Alert>>(getApiUrl(`/alerts/${id}/resolve`), resolution);
  return extractSingleData<Alert>(response.data);
};

// Dismiss alert
export const dismissAlert = async (id: string, reason: string): Promise<Alert> => {
  const response = await apiClient.put<RailsSingleWrappedResponse<Alert>>(getApiUrl(`/alerts/${id}/dismiss`), { reason });
  return extractSingleData<Alert>(response.data);
};

// Escalate alert
export const escalateAlert = async (id: string, escalation: {
  level: string;
  notes: string;
}): Promise<Alert> => {
  const response = await apiClient.put<RailsSingleWrappedResponse<Alert>>(getApiUrl(`/alerts/${id}/escalate`), escalation);
  return extractSingleData<Alert>(response.data);
};